import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import Langs, { Localize } from "../../../app/lang/langs";
import { showErrorToast } from "../../../app/utils/helpers";

// Mirrors NewsAttachmentsController: the same ceilings, so a file the user is allowed to
// pick here is a file the backend will actually accept. Rejecting locally only saves the
// round trip — the backend check is the one that counts.
export const NEWS_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const NEWS_ATTACHMENT_TYPES = [...NEWS_IMAGE_TYPES, "application/pdf"];
export const NEWS_MAX_ATTACHMENTS = 3;
const MAX_UPLOAD_BYTES = 10 * 1000 * 1000;

export function formatFileSize(bytes) {
    if (!bytes && bytes !== 0) return "";
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

/**
 * Picks the files a news post is published with: one illustration and up to three
 * documents.
 *
 * Nothing is uploaded from here. The attachment routes address a post by id, and the post
 * does not exist until it is saved, so the files are held as File objects and handed to
 * the caller, which uploads them once addNews has returned the new id.
 */
export default function NewsAttachmentsPicker({
    image,
    onImageChange,
    attachments,
    onAttachmentsChange,
    disabled = false,
}) {

    const imageInputRef = useRef(null);
    const attachmentInputRef = useRef(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    // One object URL per picked file, revoked when the picture is replaced, removed, or the
    // modal closes — otherwise the blob lives for the lifetime of the tab.
    useEffect(() => {
        if (!image) {
            setPreviewUrl(null);
            return undefined;
        }
        const url = URL.createObjectURL(image);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [image]);

    function isAcceptable(file, allowedTypes, typeErrorKey) {
        if (!allowedTypes.includes(file.type)) {
            showErrorToast(Localize(typeErrorKey));
            return false;
        }
        if (file.size > MAX_UPLOAD_BYTES) {
            showErrorToast(Localize("NEWS_FILE_TOO_LARGE"));
            return false;
        }
        return true;
    }

    function onPickImage(event) {
        const file = event.target.files && event.target.files[0];
        // Reset immediately so picking the same file twice in a row still fires onChange.
        event.target.value = "";
        if (!file) return;
        if (!isAcceptable(file, NEWS_IMAGE_TYPES, "NEWS_IMAGE_ONLY")) return;
        onImageChange(file);
    }

    function onPickAttachments(event) {
        const picked = Array.from(event.target.files || []);
        event.target.value = "";
        if (!picked.length) return;

        const room = NEWS_MAX_ATTACHMENTS - attachments.length;
        if (room <= 0) {
            showErrorToast(Localize("NEWS_ATTACHMENTS_MAX_REACHED"));
            return;
        }

        const accepted = picked
            .filter(file => isAcceptable(file, NEWS_ATTACHMENT_TYPES, "NEWS_ATTACHMENT_TYPE"))
            .slice(0, room);

        // Selecting five files when two slots are left keeps two and says so, rather than
        // silently dropping the rest.
        if (accepted.length < picked.length) {
            showErrorToast(Localize("NEWS_ATTACHMENTS_MAX_REACHED"));
        }
        if (accepted.length) onAttachmentsChange([...attachments, ...accepted]);
    }

    function onRemoveAttachment(index) {
        onAttachmentsChange(attachments.filter((_, i) => i !== index));
    }

    return (
        <Box
            sx={{
                p: 1.5,
                borderRadius: "12px",
                border: "1px solid rgba(0, 40, 128, 0.14)",
                backgroundColor: "rgba(0, 40, 128, 0.03)",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
            }}
        >
            <Box>
                <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, mb: 0.75 }}>
                    <Langs str="NEWS_IMAGE" />
                </Typography>
                {/* Both, not just the URL: `previewUrl` is state set from an effect, so it
                    still holds the old blob for the render in which `image` has just been
                    cleared. */}
                {image && previewUrl ? (
                    <Box sx={{ position: "relative", display: "inline-block" }}>
                        <Box
                            component="img"
                            src={previewUrl}
                            alt={image.name}
                            sx={{
                                display: "block",
                                maxHeight: 140,
                                maxWidth: "100%",
                                borderRadius: "10px",
                                border: "1px solid rgba(0, 40, 128, 0.12)",
                            }}
                        />
                        <Tooltip title={Localize("NEWS_ATTACHMENT_REMOVE")}>
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={disabled}
                                    onClick={() => onImageChange(null)}
                                    sx={{
                                        position: "absolute",
                                        top: 4,
                                        right: 4,
                                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                                        "&:hover": { backgroundColor: "#fff" },
                                    }}
                                >
                                    <CloseIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                ) : (
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={disabled}
                        startIcon={<AddPhotoAlternateOutlinedIcon />}
                        onClick={() => imageInputRef.current && imageInputRef.current.click()}
                        sx={{ textTransform: "none", borderRadius: "10px" }}
                    >
                        <Langs str="NEWS_ADD_IMAGE" />
                    </Button>
                )}
                <input
                    ref={imageInputRef}
                    type="file"
                    accept={NEWS_IMAGE_TYPES.join(",")}
                    style={{ display: "none" }}
                    onChange={onPickImage}
                />
            </Box>

            <Box>
                <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, mb: 0.75 }}>
                    <Langs str="ATTACHMENTS" />
                </Typography>
                {attachments.length > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
                        {attachments.map((file, index) => (
                            <Chip
                                key={`${file.name}-${index}`}
                                icon={file.type === "application/pdf"
                                    ? <PictureAsPdfOutlinedIcon />
                                    : <ImageOutlinedIcon />}
                                label={`${file.name} · ${formatFileSize(file.size)}`}
                                onDelete={disabled ? undefined : () => onRemoveAttachment(index)}
                                sx={{ maxWidth: 320, borderRadius: "10px" }}
                            />
                        ))}
                    </Box>
                )}
                <Button
                    variant="outlined"
                    size="small"
                    disabled={disabled || attachments.length >= NEWS_MAX_ATTACHMENTS}
                    startIcon={<AttachFileIcon />}
                    onClick={() => attachmentInputRef.current && attachmentInputRef.current.click()}
                    sx={{ textTransform: "none", borderRadius: "10px" }}
                >
                    <Langs str="NEWS_ADD_ATTACHMENT" />
                </Button>
                <input
                    ref={attachmentInputRef}
                    type="file"
                    multiple
                    accept={NEWS_ATTACHMENT_TYPES.join(",")}
                    style={{ display: "none" }}
                    onChange={onPickAttachments}
                />
                <Typography sx={{ fontSize: "0.75rem", color: "#6b6560", mt: 0.75 }}>
                    <Langs str="NEWS_ATTACHMENTS_HINT" />
                </Typography>
            </Box>
        </Box>
    );
}
